import React from "react";
import { useNavigate, useOutletContext } from "react-router-dom";

import { createPortal } from "react-dom";

import { useTranslation } from "react-i18next";

import { useAuth0 } from "@auth0/auth0-react";

import { Copy, Check, KeyRound } from "lucide-react";

import "./RegisterOssReviewSubmitPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const ACCESS_STORAGE_KEY = "legup_oss_access_context";

const COMPANY_DETAILS_STORAGE_KEY = "legup_oss_company_details";

const SERVICES_STORAGE_KEY = "legup_oss_services";

const AUTH_CONNECTION_KEY = "legup_auth_connection";

const OSS_AUTH_CONNECTION = "LEGUP-OSS";

function readStorage(key, fallback = {}) {

  try {

    return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback));

  } catch {

    return fallback;

  }

}

function normalizeServices(rawServices) {

  if (!rawServices) {

    return [];

  }

  if (Array.isArray(rawServices)) {

    return rawServices.filter((item) => item.service_type);

  }

  if (

    typeof rawServices === "object" &&

    !rawServices.paidServices &&

    !rawServices.nonPaidServices &&

    !rawServices.paid &&

    !rawServices.non_paid

  ) {

    return Object.values(rawServices).filter((item) => item.service_type);

  }

  const paid = rawServices?.paidServices || rawServices?.paid || [];

  const nonPaid = rawServices?.nonPaidServices || rawServices?.non_paid || [];

  return [

    ...paid.map((item) => ({

      ...item,

      service_type: "PAID",

    })),

    ...nonPaid.map((item) => ({

      ...item,

      service_type: "NON_PAID",

      subsidy_status: "NOT_APPLICABLE",

      approximate_cost_text: "",

      subsidy_information_url: "",

      subsidy_information_label: "",

      subsidy_notes: "",

    })),

  ];

}

function getServiceCode(item) {

  return item.service_code || item.code || item.service?.code || "";

}

function getServiceLabel(item) {

  return (

    item.label ||

    item.label_en ||

    item.service_label ||

    item.service?.label ||

    item.service_code ||

    item.code ||

    "—"

  );

}

export default function RegisterOssReviewSubmitPage() {

  const { t } = useTranslation("oss-registration-review");
  const navigate = useNavigate();
  const { ossPassword, clearOssPassword } = useOutletContext();

  const { loginWithRedirect } = useAuth0();

  const [accessContext] = React.useState(() => readStorage(ACCESS_STORAGE_KEY));

  const [companyDetails] = React.useState(() =>

    readStorage(COMPANY_DETAILS_STORAGE_KEY),

  );

  const [servicesRaw] = React.useState(() =>

    readStorage(SERVICES_STORAGE_KEY, []),

  );

  const [loading, setLoading] = React.useState(false);

  const [err, setErr] = React.useState("");

  const [ok, setOk] = React.useState("");

  const [companyKey, setCompanyKey] = React.useState("");

  const [companyKeyCopied, setCompanyKeyCopied] = React.useState(false);

  const [registeredEmail, setRegisteredEmail] = React.useState("");

  const [copyError, setCopyError] = React.useState("");

  const services = React.useMemo(

    () => normalizeServices(servicesRaw),

    [servicesRaw],

  );

  const paidServices = services.filter(

    (service) => service.service_type === "PAID",

  );

  const nonPaidServices = services.filter(

    (service) => service.service_type === "NON_PAID",

  );


  React.useEffect(() => {

    if (!companyKey) {

      return undefined;

    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {

      document.body.style.overflow = previousOverflow;

    };

  }, [companyKey]);

  async function createAccount() {

    setErr("");

    setOk("");

    setCopyError("");

    const email = companyDetails.email || accessContext.email;

    const accessCode = accessContext.access_code;

    if (!email || !accessCode) {

      setErr(t("messages.missingAccessContext"));

      return;

    }

    if (!ossPassword) {

      setErr(t("messages.passwordExpired", "For security, your password is not retained after a page refresh. Return to Company Details and enter it again."));

      return;

    }

    if (!services.length) {

      setErr(t("messages.missingServices"));

      return;

    }

    if (!companyDetails.company_address || !companyDetails.company_address_place_id) {
      setErr(
        t(
          "messages.selectVerifiedAddress",
          "Please return to Company Details and select a verified street address.",
        ),
      );
      return;
    }

    const payload = {

      email,

      access_code: accessCode,

      password: ossPassword,

      first_name: companyDetails.first_name,

      last_name: companyDetails.last_name,

      phone: companyDetails.phone,

      company_address: companyDetails.company_address,
      company_address_place_id: companyDetails.company_address_place_id,

      vat_number: companyDetails.vat_number,

      company_name: companyDetails.company_name,

      country_code: companyDetails.country_code,

      role_in_company: companyDetails.role_in_company,

      additional_notes: companyDetails.additional_notes,

      short_description: companyDetails.additional_notes || "",

      public_profile_summary: companyDetails.additional_notes || "",

      services: services.map((item) => ({

        service_code: getServiceCode(item),

        service_type: item.service_type,

        subsidy_status:

          item.service_type === "PAID"

            ? item.subsidy_status || "NOT_APPLICABLE"

            : "NOT_APPLICABLE",

        approximate_cost_text:

          item.service_type === "PAID" ? item.approximate_cost_text || "" : "",

        subsidy_information_url:

          item.service_type === "PAID"

            ? item.subsidy_information_url || ""

            : "",

        subsidy_information_label:

          item.service_type === "PAID"

            ? item.subsidy_information_label || ""

            : "",

        subsidy_notes:

          item.service_type === "PAID" ? item.subsidy_notes || "" : "",

      })),

    };

    setLoading(true);

    try {

      const response = await fetch(

        `${API_BASE}/api/oss/register-with-access-code/`,

        {

          method: "POST",

          headers: {

            "Content-Type": "application/json",

          },

          body: JSON.stringify(payload),

        },

      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {

        setErr(

          data?.detail ||

            data?.email?.[0] ||

            data?.services?.[0] ||

            data?.services ||

            data?.password?.[0] ||

            data?.access_code?.[0] ||

            data?.error ||

            t("messages.createFailed"),

        );

        return;

      }

      const returnedCompanyKey = String(data?.company_key || "").trim();

      if (!returnedCompanyKey) {

        setErr(

          t(

            "messages.companyKeyMissing",

            "Your OSS account was created, but the company key could not be displayed. Please contact the platform administrator.",

          ),

        );

        return;

      }


      localStorage.removeItem(ACCESS_STORAGE_KEY);

      localStorage.removeItem(COMPANY_DETAILS_STORAGE_KEY);

      localStorage.removeItem(SERVICES_STORAGE_KEY);
      clearOssPassword();

      setRegisteredEmail(email);

      setCompanyKey(returnedCompanyKey);

      setCompanyKeyCopied(false);

      setCopyError("");

      setOk(

        t(

          "messages.createdWithKey",

          "Your OSS account has been created successfully.",

        ),

      );

    } catch (error) {

      setErr(error?.message || String(error));

    } finally {

      setLoading(false);

    }

  }

  async function copyCompanyKey() {

    if (!companyKey) {

      return;

    }

    setCopyError("");

    try {

      if (navigator.clipboard && navigator.clipboard.writeText) {

        await navigator.clipboard.writeText(companyKey);

      } else {


        const textarea = document.createElement("textarea");

        textarea.value = companyKey;

        textarea.setAttribute("readonly", "");

        textarea.style.position = "fixed";

        textarea.style.opacity = "0";

        document.body.appendChild(textarea);

        textarea.select();

        const copied = document.execCommand("copy");

        document.body.removeChild(textarea);

        if (!copied) {

          throw new Error("Copy command failed.");

        }

      }

      setCompanyKeyCopied(true);

      window.setTimeout(() => {

        setCompanyKeyCopied(false);

      }, 2000);

    } catch (error) {

      console.error("Could not copy OSS company key:", error);

      setCopyError(

        t(

          "messages.copyFailed",

          "Could not copy the company key automatically. Please select and copy it manually.",

        ),

      );

    }

  }

  function continueToLogin() {

    sessionStorage.setItem(AUTH_CONNECTION_KEY, OSS_AUTH_CONNECTION);

    loginWithRedirect({

      appState: {

        returnTo: "/post-login",

      },

      authorizationParams: {

        prompt: "login",

        screen_hint: "login",

        connection: OSS_AUTH_CONNECTION,

        login_hint: registeredEmail,

      },

    });

  }

  const companyKeyModal =

    companyKey && typeof document !== "undefined"

      ? createPortal(

          <div className="oss-company-key-modal-backdrop" role="presentation">

            <div

              className="oss-company-key-modal"

              role="dialog"

              aria-modal="true"

              aria-labelledby="oss-company-key-modal-title"

              aria-describedby="oss-company-key-modal-description"

            >

              <div className="oss-company-key-modal__icon">

                <KeyRound size={30} />

              </div>

              <div className="oss-company-key-modal__heading">

                <span className="oss-company-key-modal__eyebrow">

                  {t("companyKey.eyebrow", "Registration completed")}

                </span>

                <h2 id="oss-company-key-modal-title">

                  {t("companyKey.title", "Your OSS company key")}

                </h2>

                <p id="oss-company-key-modal-description">

                  {t(

                    "companyKey.description",

                    "Your One-Stop Shop has been created successfully. Save this company key securely before continuing.",

                  )}

                </p>

              </div>

              <div className="oss-company-key-modal__key-section">

                <span className="oss-company-key-modal__label">

                  {t("companyKey.label", "Company key")}

                </span>

                <div className="oss-company-key-modal__key-row">

                  <code>{companyKey}</code>

                  <button

                    type="button"

                    className={

                      companyKeyCopied

                        ? "oss-company-key-modal__copy oss-company-key-modal__copy--copied"

                        : "oss-company-key-modal__copy"

                    }

                    onClick={copyCompanyKey}

                  >

                    {companyKeyCopied ? (

                      <>

                        <Check size={17} />

                        <span>{t("companyKey.copied", "Copied")}</span>

                      </>

                    ) : (

                      <>

                        <Copy size={17} />

                        <span>{t("companyKey.copy", "Copy key")}</span>

                      </>

                    )}

                  </button>

                </div>

                {copyError && (

                  <div className="oss-company-key-modal__copy-error">

                    {copyError}

                  </div>

                )}

              </div>

              <div className="oss-company-key-modal__notice">

                <strong>

                  {t("companyKey.noticeTitle", "Keep this key secure")}

                </strong>

                <p>

                  {t(

                    "companyKey.notice",

                    "Authorised employees of your organisation will use this key when creating their own LEG-UP account and joining this OSS. Share it only with members of your organisation who should have access.",

                  )}

                </p>

              </div>

              <div className="oss-company-key-modal__actions">

                <button

                  type="button"

                  className="oss-company-key-modal__continue"

                  onClick={continueToLogin}

                >

                  {t("companyKey.continue", "Continue to sign in")}

                </button>

              </div>

              <p className="oss-company-key-modal__footer-note">

                {t(

                  "companyKey.footerNote",

                  "You will also be able to reveal the company key later from your OSS account, subject to your organisation permissions.",

                )}

              </p>

            </div>

          </div>,

          document.body,

        )

      : null;

  return (

    <>

      <div className="oss-review-page">

        <section className="oss-review-hero">

          <p className="oss-review-kicker">{t("hero.kicker")}</p>

          <h1>{t("hero.title")}</h1>

          <p>{t("hero.subtitle")}</p>

        </section>

        <div className="oss-review-grid">

          <section className="oss-review-card">

            <div className="oss-review-card-header">

              <h2>{t("sections.account")}</h2>

              <p>{t("sections.accountSubtitle")}</p>

            </div>

            <div className="oss-review-list">

              <div>

                <span>{t("fields.name")}</span>

                <strong>

                  {companyDetails.first_name || "—"}{" "}

                  {companyDetails.last_name || ""}

                </strong>

              </div>

              <div>

                <span>{t("fields.email")}</span>

                <strong>

                  {companyDetails.email || accessContext.email || "—"}

                </strong>

              </div>

              <div>

                <span>{t("fields.company")}</span>

                <strong>{companyDetails.company_name || "—"}</strong>

              </div>

              <div>

                <span>{t("fields.country")}</span>

                <strong>{companyDetails.country_code || "—"}</strong>

              </div>

              <div>

                <span>{t("fields.vat")}</span>

                <strong>{companyDetails.vat_number || "—"}</strong>

              </div>

              <div>

                <span>{t("fields.role")}</span>

                <strong>{companyDetails.role_in_company || "—"}</strong>

              </div>

              <div>

                <span>{t("fields.phone")}</span>

                <strong>{companyDetails.phone || "—"}</strong>

              </div>

              <div>

                <span>{t("fields.address")}</span>

                <strong>{companyDetails.company_address || "—"}</strong>

              </div>

            </div>

          </section>

          <section className="oss-review-card">

            <div className="oss-review-card-header">

              <h2>{t("sections.services")}</h2>

              <p>

                {t("summary.paid", {

                  count: paidServices.length,

                })}{" "}

                ·{" "}

                {t("summary.nonPaid", {

                  count: nonPaidServices.length,

                })}

              </p>

            </div>

            <div className="oss-review-services">

              <div className="oss-review-service-column">

                <h3>{t("sections.paidServices")}</h3>

                {paidServices.length === 0 ? (

                  <p className="oss-review-empty">

                    {t("messages.noPaidServices")}

                  </p>

                ) : (

                  paidServices.map((service) => (

                    <div

                      className="oss-review-service"

                      key={`paid-${getServiceCode(service)}`}

                    >

                      <strong>{getServiceLabel(service)}</strong>

                      <span>

                        {t("fields.cost")}:{" "}

                        {service.approximate_cost_text

                          ? `${service.approximate_cost_text} €`

                          : "—"}

                      </span>

                      <span>

                        {t("fields.subsidy")}:{" "}

                        {service.subsidy_status === "YES"

                          ? t("common.yes")

                          : t("common.no")}

                      </span>

                    </div>

                  ))

                )}

              </div>

              <div className="oss-review-service-column">

                <h3>{t("sections.nonPaidServices")}</h3>

                {nonPaidServices.length === 0 ? (

                  <p className="oss-review-empty">

                    {t("messages.noNonPaidServices")}

                  </p>

                ) : (

                  nonPaidServices.map((service) => (

                    <div

                      className="oss-review-service"

                      key={`non-paid-${getServiceCode(service)}`}

                    >

                      <strong>{getServiceLabel(service)}</strong>

                      <span>{t("fields.freeOfCharge")}</span>

                    </div>

                  ))

                )}

              </div>

            </div>

          </section>

        </div>

        {companyDetails.additional_notes && (

          <section className="oss-review-card oss-review-notes">

            <h2>{t("sections.notes")}</h2>

            <p>{companyDetails.additional_notes}</p>

          </section>

        )}

        {!ossPassword && !companyKey && (
        <div className="oss-review-message-area">
          <div className="oss-review-message error" role="status">
            {t(
              "messages.passwordExpired",
              "For security, your password is not retained after a page refresh. Return to Company Details and enter it again.",
            )}{" "}
            <button
              type="button"
              onClick={() => navigate("/register/oss/company-details")}
            >
              {t("buttons.returnToCompanyDetails", "Return to Company Details")}
            </button>
          </div>
        </div>
      )}

      {(err || ok) && !companyKey && (

          <div className="oss-review-message-area">

            {err && <div className="oss-review-message error">{err}</div>}

            {ok && <div className="oss-review-message success">{ok}</div>}

          </div>

        )}

        {!companyKey && (

          <div className="oss-review-footer">

            <p>{t("footer.help")}</p>

            <button type="button" onClick={createAccount} disabled={loading}>

              {loading ? t("buttons.creating") : t("buttons.createAccount")}

            </button>

          </div>

        )}

      </div>

      {companyKeyModal}

    </>

  );

}
